/**
 * Binance WebSocket Streams Configuration
 *
 * WebSocket channels, topology info, and stream constants for Binance.
 *
 * IMPORTANT: This file configures WebSocket STREAMS, which is different from WebSocket API:
 *
 * | Feature | WebSocket Streams (this file) | WebSocket API (ws-api.ts) |
 * |---------|-------------------------------|---------------------------|
 * | URL | `wss://stream.binance.com:9443/ws` | `wss://ws-api.binance.com:443/ws-api/v3` |
 * | Protocol | Pure streaming (subscribe) | Request/Response |
 * | Klines | `<symbol>@kline_<interval>` | `method: "klines"` with params |
 * | Use Case | Real-time continuous updates | On-demand queries |
 * | Parameters | symbol + interval only | Full query params (timeZone, etc.) |
 *
 * For on-demand kline queries with timeZone support, see `ws-api.ts`.
 *
 * @see https://developers.binance.com/docs/binance-spot-api-docs/web-socket-streams
 */

import type { WsChannelDescriptor, WsTopologyInfo } from '../../lib/types';

export const BINANCE_WS_BASE_URL = 'wss://stream.binance.com:9443/ws';
export const BINANCE_WS_COMBINED_BASE_URL = 'wss://stream.binance.com:9443/stream';

/**
 * @see https://developers.binance.com/docs/binance-spot-api-docs/web-socket-streams
 */
export const BINANCE_WS_CHANNELS: Record<string, WsChannelDescriptor> = {
  depth: {
    channel: 'depth',
    topic: '<symbol>@depth',
    supportsBatching: true,
    docUrl:
      'https://developers.binance.com/docs/binance-spot-api-docs/web-socket-streams#partial-book-depth-streams',
  },
  bookTicker: {
    channel: 'bookTicker',
    topic: '<symbol>@bookTicker',
    supportsBatching: true,
    docUrl:
      'https://developers.binance.com/docs/binance-spot-api-docs/web-socket-streams#individual-symbol-book-ticker-streams',
  },
  ticker: {
    channel: 'ticker24h',
    topic: '<symbol>@ticker',
    supportsBatching: true,
    docUrl:
      'https://developers.binance.com/docs/binance-spot-api-docs/web-socket-streams#individual-symbol-ticker-streams',
  },
  trades: {
    channel: 'trades',
    topic: '<symbol>@trade',
    supportsBatching: true,
    docUrl:
      'https://developers.binance.com/docs/binance-spot-api-docs/web-socket-streams#trade-streams',
  },
  klines: {
    channel: 'klines',
    topic: '<symbol>@kline_<interval>',
    supportsBatching: true,
    docUrl:
      'https://developers.binance.com/docs/binance-spot-api-docs/web-socket-streams#klinecandlestick-streams',
  },
};

export const BINANCE_WS_TOPOLOGY: WsTopologyInfo = {
  baseUrl: BINANCE_WS_BASE_URL,
  singleConnectionPerChannel: false,
  heartbeatIntervalMs: 3_000,
  note: 'Recommended pattern: keep a single connection per symbol and combine multiple streams via the /stream endpoint.',
};
